import * as React from "react";
import { Flex, Heading, Text } from "@radix-ui/themes";
import {
  ExpiredTokenError,
  FetchError,
  IncorrectPermissionsError,
  NoAuthTokenError,
} from "./errors.js";
import { Cross2Icon } from "@radix-ui/react-icons";
import { useTranslation } from "./i18n/use-translation.js";

type GenericErrorProps = React.ComponentProps<typeof Flex> & {
  error: unknown;
};

export function GenericError({ error, ...domProps }: GenericErrorProps) {
  const translate = useTranslation();
  React.useEffect(() => {
    console.error(error);
  }, [error]);

  const { heading, message } = getErrorMessage(error, translate);
  return (
    <Flex
      p="6"
      justify="center"
      align="center"
      direction="column"
      {...domProps}
    >
      <Flex
        align="center"
        justify="center"
        width="32px"
        height="32px"
        mb="2"
        style={{
          borderRadius: "9999px",
          backgroundColor: "var(--red-a4)",
          color: "var(--red-a11)",
        }}
      >
        <Cross2Icon width="24px" height="24px" />
      </Flex>

      <Flex direction="column" gap="1" maxWidth="420px">
        <Heading size="5" align="center" mb="1" wrap="balance" as="h3">
          {heading}
        </Heading>
        <Text as="p" align="center" wrap="balance" color="gray">
          {message}
        </Text>
      </Flex>
    </Flex>
  );
}

export function getErrorMessage(
  error: unknown,
  translate: ({
    defaultMessage,
    description,
    id,
  }: {
    defaultMessage: string;
    description: string;
    id: string;
  }) => string,
) {
  if (error instanceof FetchError) {
    return {
      heading: translate({
        defaultMessage: "Error fetching data",
        description: "Error message heading",
        id: "5QzKdW",
      }),
      message: translate({
        defaultMessage:
          "An error occurred. You may need to configure CORS in the WorkOS Dashboard.",
        description: "Error message details",
        id: "63rRud",
      }),
    };
  }

  if (error instanceof NoAuthTokenError) {
    return {
      heading: translate({
        defaultMessage: "Authorization error",
        description: "Error message heading",
        id: "s/dQVI",
      }),
      message: translate({
        defaultMessage:
          "You likely forgot to provide an authorization token to the widget.",
        description: "Error message details",
        id: "d7mvgq",
      }),
    };
  }

  if (error instanceof ExpiredTokenError) {
    return {
      heading: translate({
        defaultMessage: "Session expired",
        description:
          "Error message heading shown when the widget access token has expired",
        id: "O2RRtR",
      }),
      message: translate({
        defaultMessage: "Please reload the page to continue.",
        description:
          "Error message details shown when the widget access token has expired",
        id: "IsBzv7",
      }),
    };
  }

  if (error instanceof IncorrectPermissionsError) {
    return {
      heading: translate({
        defaultMessage: "Incorrect permissions",
        description: "Error message heading",
        id: "dFmPj3",
      }),
      message: translate({
        defaultMessage:
          "Check that the correct permissions have been assigned.",
        description: "Error message details",
        id: "hd/Llp",
      }),
    };
  }

  return {
    heading: translate({
      defaultMessage: "Error fetching data",
      description: "Error message heading",
      id: "5QzKdW",
    }),
    message: translate({
      defaultMessage:
        "An unexpected error occurred. Check your network connection or try refreshing the page.",
      description: "Error message details",
      id: "t+CDVJ",
    }),
  };
}
